import { useEffect, useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { CheckCircle2, Loader2, ArrowRight } from 'lucide-react'
import { useAuth } from '@/features/auth'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { Logo } from '@/components/Logo'

export const Route = createFileRoute('/welcome')({
  component: WelcomePage,
})

const API_URL =
  (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ??
  'https://api.neurodivergente.secbra.in'

/**
 * Página pós-confirmação de e-mail. Se o usuário chegou aqui autenticado mas
 * sem organização (provisionamento faltou por falha transitória), chama
 * /api/public/signup/complete (idempotente) para provisionar.
 */
function WelcomePage() {
  useDocumentTitle('Bem-vindo')
  const navigate = useNavigate()
  const { session, loading } = useAuth()
  const [provisioning, setProvisioning] = useState(false)
  const [provisionError, setProvisionError] = useState<string | null>(null)

  useEffect(() => {
    if (!loading && !session) {
      navigate({ to: '/login' })
    }
  }, [loading, session, navigate])

  const completeSignup = async () => {
    if (!session) return
    setProvisioning(true)
    setProvisionError(null)
    try {
      const res = await fetch(`${API_URL}/api/public/signup/complete`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        setProvisionError(body?.message ?? 'Não foi possível concluir a configuração.')
        return
      }
      navigate({ to: '/dashboard' })
    } catch {
      setProvisionError('Erro de conexão. Tente novamente.')
    } finally {
      setProvisioning(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-pulse">
          <Logo size={64} />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md text-center">
        <div className="inline-flex items-center justify-center mb-4">
          <Logo size={64} />
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8">
          <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-800 mb-2 font-poppins">
            E-mail confirmado!
          </h1>
          <p className="text-gray-600 mb-6">
            Sua conta está ativa. Se a sua organização ainda não apareceu no painel,
            clique abaixo para concluir a configuração automaticamente.
          </p>

          {provisionError && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 mb-4">
              {provisionError}
            </div>
          )}

          <button
            onClick={completeSignup}
            disabled={provisioning}
            className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-lg bg-teal-600 text-white font-medium hover:bg-teal-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {provisioning ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                Ir para o painel
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>

          <Link to="/login" className="block mt-4 text-sm text-gray-500 hover:text-gray-700">
            Já tem outra conta? Fazer login
          </Link>
        </div>
      </div>
    </div>
  )
}
